/*
 * @Author: linguang linguang@nextmar.com
 * @Date: 2025-03-12 12:02:41
 * @LastEditors: Please set LastEditors
 * @LastEditTime: 2025-09-29 19:33:52
 * @FilePath: /src/store/slices/userSlice.ts
 * @Description: 这是默认设置,请设置`customMade`, 打开koroFileHeader查看配置 进行设置: https://github.com/OBKoro1/koro1FileHeader/wiki/%E9%85%8D%E7%BD%AE
 */
import { fetchCustomerAccount, fetchExitMember, userSource as userSourceApi } from '@/api/account';
import { User } from '@/api/types/public';
import { ProviderType, ReportingCenter } from '@/reporting';
import { getZeeloolSnowplowClient } from '@/reporting/integrations/snowplow-sdk/client';
import { ApplovinProvider } from '@/reporting/providers/applovinProvider';
import { AwinProvider } from '@/reporting/providers/awinProvider';
import { PepperjamProvider } from '@/reporting/providers/pepperjamProvider';
import { EmarsysProvider } from '@/reporting/providers/emarsysProvider';
import { ImpactRadiusProvider } from '@/reporting/providers/impactProvider';
import { SensorsProvider } from '@/reporting/providers/sensorProvider';
import { Store } from '@/store';
import logger from '@/utils/client-logger';
import { ZL_OLDLEVEL, ZL_USERINFO } from '@/utils/constants';
import { APP_CLEAR_TOKEN_SIGNAL } from '@/utils/enums/app-sdk';
import { fetchMemberRelatedPublicResourceConfigNames } from '@/utils/refresh-member-public-resource';
import cookies from 'js-cookie';
import { StateCreator } from 'zustand';

export interface UtmSource {
  utm_source?: string;
  utm_medium?: string;
  utm_term?: string;
  utm_id?: string;
  utm_campaign?: string;
}

/** 控制账户刷新完成后是否额外上报静默注册成功事件。 */
export interface RefreshAccountInfoOptions {
  reportSnowplowSignUp?: boolean;
}

export interface UserState {
  username: string;
  token: string;
  /**
   * 客户端是否已完成当前路由轮次下从 cookie 同步 token（含确认无 token）。
   * 路由变化时由 GlobalInitializer 先置 false，同步结束后再置 true；避免子组件误判登录态。
   */
  clientTokenReady: boolean;
  visitorId: string;
  userInfo: User;
  userInfoReady: boolean;
  oldMemberInfo: {
    score: number;
    level: number;
  }; // 旧用户等级信息
  utmSource: UtmSource; // UTM参数
}

export interface UserActions {
  setClientTokenReady: (ready: UserState['clientTokenReady']) => void;
  syncAppLoginToken: (token: unknown) => Promise<AppLoginTokenSyncResult>;
  syncAppNavigationToken: (token: unknown) => Promise<AppLoginTokenSyncResult>;
  // 登录成功之后设置用户token，这个只可能在登录成功之后调用一次
  setToken: (token: UserState['token']) => void;
  setVisitorId: (visitorId: UserState['visitorId']) => void;
  // 登录成功之后需要设置这里的用户信息，这个只可能在登录成功之后调用一次
  setUserInfo: (userInfo: UserState['userInfo']) => void;
  // 登录态保持下刷新用户的信息，可能会调用多次
  userInfoReady: boolean;
  setUserInfoReady: (ready: UserState['userInfoReady']) => void;
  refreshAccountInfo: (options?: RefreshAccountInfoOptions) => Promise<User | null>;
  exitMember: () => Promise<void>;
  handleUserDataEffect: () => void;
  setUtmSource: (utmSource: UserState['utmSource']) => void;
  userSource: () => Promise<void>;
}

export type UserSlice = UserState & UserActions;

export type AppLoginTokenSyncResult = 'authenticated' | 'cleared';

type AppTokenSyncSource = 'login-callback' | 'navigation-return';

type AppTokenSyncActions = Pick<
  UserActions,
  'setToken' | 'setUserInfo' | 'setClientTokenReady' | 'refreshAccountInfo'
>;

async function syncAppToken(
  token: unknown,
  source: AppTokenSyncSource,
  get: () => AppTokenSyncActions,
): Promise<AppLoginTokenSyncResult> {
  const isInvalidToken =
    typeof token !== 'string' || token === '' || token === APP_CLEAR_TOKEN_SIGNAL;

  if (isInvalidToken) {
    await get().setToken('');
    get().setUserInfo({});
    get().setClientTokenReady(true);
    return 'cleared';
  }

  const shouldSkipAccountRefresh =
    source === 'navigation-return' && token === localStorage?.getItem('zl-token');
  if (shouldSkipAccountRefresh) {
    get().setClientTokenReady(true);
    return 'authenticated';
  }

  await get().setToken(token);
  get().setClientTokenReady(true);
  await get().refreshAccountInfo();
  return 'authenticated';
}

/** 用户切片的初始状态。 */
export const initialUserState: UserState = {
  username: '',
  token: '',
  clientTokenReady: false,
  visitorId: '',
  userInfo: {},
  userInfoReady: false,
  oldMemberInfo: {
    score: 0,
    level: 0,
  }, // 旧用户等级信息
  utmSource: {}, // UTM参数初始值
};

/** 判断 SignUp 身份是否包含 C0、DEL 或 C1 控制字符。 */
function containsSignUpIdentityControlCharacter(value: string): boolean {
  return Array.from(value).some((character) => {
    const codePoint = character.charCodeAt(0);

    // C0 为 0-31，DEL/C1 为 127-159。
    return codePoint <= 31 || (codePoint >= 127 && codePoint <= 159);
  });
}

/** 把账户注册身份规范化为非空、非字符串 nullish 的值。 */
function normalizeSignUpIdentityValue(value: unknown): string | null {
  // 真正的 nullish 值没有可追踪身份。
  if (value === null || value === undefined) return null;

  const rawValue = String(value);

  // 必须在 trim 前拒绝首尾控制字符，不能把非法身份清洗成可发送值。
  if (containsSignUpIdentityControlCharacter(rawValue)) return null;

  const normalizedValue = rawValue.trim();
  const lowerCaseValue = normalizedValue.toLowerCase();

  // 空字符串和大小写任意的字符串 nullish 值都应拒绝。
  if (!normalizedValue || lowerCaseValue === 'null' || lowerCaseValue === 'undefined') return null;

  return normalizedValue;
}

/** 非阻塞调用 SDK 专用 SignUp，并消费同步与异步异常。 */
function trackRefreshedAccountSignUpSafely(account: User): void {
  const uuid = normalizeSignUpIdentityValue(account.uuid);
  const customerId = normalizeSignUpIdentityValue(account.id);

  // 静默注册只有两个规范化身份齐全时才允许发送。
  if (!uuid || !customerId) return;

  const client = getZeeloolSnowplowClient();

  // singleton 尚未配置时账户刷新与返回值保持不变。
  if (!client) return;

  try {
    void client.trackSignUp({ uuid, customerId }).catch(() => {
      // Promise rejection 只记录稳定类别，不泄漏身份或底层异常。
      logger.error('[userSlice.refreshAccountInfo] signup_track_failed');
    });
  } catch {
    // SDK 同步异常同样不能阻断账户缓存和支付流程。
    logger.error('[userSlice.refreshAccountInfo] signup_track_failed');
  }
}

/** 创建用户状态与认证、刷新、营销绑定相关动作。 */
export const createUserSlice: StateCreator<Store, [], [], UserSlice> = (set, get) => ({
  ...initialUserState,
  setClientTokenReady: (ready) => set({ clientTokenReady: ready }),
  syncAppLoginToken: (token) => syncAppToken(token, 'login-callback', get),
  syncAppNavigationToken: (token) => syncAppToken(token, 'navigation-return', get),
  setToken: async (token) => {
    set({ token });
    // 如果是在iframe中，则不存储到localStorage
    if (window.self === window.top) {
      // 存储到localStorage
      if (token) {
        localStorage.setItem('zl-token', token);
        // 注册邀请中间过程邀请字段丢失导致用户未被邀请成功问题排查
        localStorage.setItem(
          'zl-mw_reward',
          JSON.stringify({ value: null, expired: new Date(0).getTime() }),
        );
        // setCookie('token', token);
        // token Cookie 默认有效期为 365 天
        cookies.set('token', token, { expires: 365 });
      } else {
        localStorage.removeItem('zl-token');
        cookies.set('token', '', { expires: 0 });
      }
    }
  },
  setUserInfoReady: (ready) => set({ userInfoReady: ready }),
  setVisitorId: (visitorId) => {
    set({ visitorId });
    if (visitorId) {
      localStorage.setItem('zl-visitor_id', visitorId);
    } else {
      localStorage.removeItem('zl-visitor_id');
    }
  },

  handleUserDataEffect: () => {
    const user = get().userInfo;

    // 存储到localStorage
    if (user && Object.keys(user).length > 0) {
      localStorage.setItem(ZL_USERINFO, JSON.stringify(user));
      localStorage.setItem('zl-userId', user.id?.toString() || '');
      const center = ReportingCenter.getInstance();
      try {
        const emarsysProvider = center.getProvider(ProviderType.EMARSYS) as EmarsysProvider;
        if (emarsysProvider && user.id) {
          emarsysProvider.track({
            eventName: 'setCustomerId',
            properties: user.id.toString(),
          });
        }
      } catch (error) {
        logger.error('Failed to track login success:', error);
      }

      try {
        const aleid = localStorage.getItem('zl_axon_aleid');
        const applovinProvider = center.getProvider(ProviderType.APPLOVIN) as ApplovinProvider;
        if (applovinProvider) {
          applovinProvider.track({
            eventName: 'generate_lead',
            properties: {
              user_id: `${user.id}&${aleid}`,
            },
          });
        }
      } catch (e) {
        logger.error('Failed to track login success:', e);
      }
      // 神策登录成功埋点
      try {
        const sensorsProvider = center.getProvider(ProviderType.SENSORS) as SensorsProvider;
        if (sensorsProvider && user.uuid) {
          const snowplowUserId =
            user.id !== undefined && user.id !== null ? user.id.toString() : undefined;
          sensorsProvider.sensorsLogin(user.uuid.toString(), snowplowUserId);
        }
      } catch (error) {
        logger.error('Failed to track login success:', error);
      }
      // 绑定impact
      const impactProvider = center.getProvider(ProviderType.IMPACT_RADIUS) as ImpactRadiusProvider;
      if (impactProvider) {
        impactProvider.bindImpact(() => {
          get().refreshAccountInfo();
        });
      }
      // 绑定awin
      const awinProvider = center.getProvider(ProviderType.AWIN) as AwinProvider;
      if (awinProvider) {
        awinProvider.bindAwin(get().token, () => {
          get().refreshAccountInfo();
        });
      }
      // 绑定 Pepperjam
      const pepperjamProvider = center.getProvider(ProviderType.PEPPERJAM) as PepperjamProvider;
      if (pepperjamProvider) {
        pepperjamProvider.bindPepperjam(get().token, () => {
          get().refreshAccountInfo();
        });
      }
    } else {
      localStorage.removeItem(ZL_USERINFO);
      localStorage.removeItem('zl-userId');
    }
  },
  setUserInfo: (userInfo) => {
    set({ userInfo });
    if (window.self === window.top) {
      get().handleUserDataEffect();
    }
  },

  // 刷新用户账户信息 - 从account-setting-store合并
  refreshAccountInfo: async (options = {}) => {
    try {
      const { code, data } = await fetchCustomerAccount();
      if (code === 200) {
        set({
          userInfo: data,
        });
        if (data && Object.keys(data).length > 0) {
          localStorage.setItem(ZL_USERINFO, JSON.stringify(data));
          localStorage.setItem('zl-userId', data.id?.toString() || '');
        }
        // 保存用户老的等级，用于支付成功页判断是否出升级提示
        if (typeof window !== 'undefined') {
          localStorage.setItem(
            ZL_OLDLEVEL,
            JSON.stringify({
              level: data.member_level || 0,
              score: data.member_score || 0,
            }),
          );
        }
        if (options.reportSnowplowSignUp) {
          // 账户与本地缓存更新完成后，再把规范化身份交给 SDK。
          trackRefreshedAccountSignUpSafely(data);
        }
        return data;
      }
    } catch (error) {
      logger.error('Failed to refresh account info:', error);
    }

    return null;
  },

  // 退会
  exitMember: async () => {
    try {
      const { code: exitCode } = await fetchExitMember();
      if (exitCode === 200) {
        const { code, data } = await fetchCustomerAccount();
        if (code === 200) {
          set({
            userInfo: data,
            oldMemberInfo: {
              score: data.member_score,
              level: data.member_level,
            },
          });
          const memberConfigNames = await fetchMemberRelatedPublicResourceConfigNames();
          if (memberConfigNames) {
            const prevPublicResourceData = get().publicResourceData;
            set({
              publicResourceData: {
                ...prevPublicResourceData,
                config_names: {
                  ...(prevPublicResourceData.config_names || {}),
                  ...memberConfigNames,
                },
              },
            });
          }
        }
      }
    } catch (error) {
      logger.error('Failed to exit member:', error);
    }
  },

  // 设置UTM参数
  setUtmSource: (utmSource) => {
    set({ utmSource });
  },
  /**
   * 需求：https://tapd.nextmar.com/2/needs/view?id=1006121
   * 登录成功后，检测是否从 tiktok 渠道过来的用户，如果是则接口请求记录
   * 接口请求成功后则删除缓存
   */
  userSource: async () => {
    const key = 'zl_tiktok_user_source';
    const data = localStorage.getItem(key);
    if (data) {
      try {
        const params = JSON.parse(data);
        if (params) {
          const { code } = (await userSourceApi(params)) as any;
          if (code === 200) {
            localStorage.removeItem(key);
          }
        }
      } catch (error) {
        logger.error('User source tracking failed:', error);
      }
    }
  },
});
